import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api'; // Assuming your api file is at ../api

// Create the context
const AuthContext = createContext(null);

// Helper function to clean roles
const cleanRoles = (rolesArray) => {
    if (!rolesArray || !Array.isArray(rolesArray)) {
        return [];
    }
    return rolesArray.map(role => 
        role.startsWith("ROLE_") ? role.substring(5) : role
    );
};

// Create the provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);

    // This runs ONCE when the app loads to check localStorage
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                
                // Check if token is expired
                if (payload.exp * 1000 > Date.now()) {
                    
                    // 👇 --- FIX #1 ---
                    // Clean the roles before setting the user
                    const cleanedUserRoles = cleanRoles(payload.authorities); 
                    
                    setUser({ 
                        username: payload.sub,
                        roles: cleanedUserRoles // Use the cleaned roles
                    });
                } else {
                    // Token is expired
                    localStorage.removeItem('token');
                }
            } catch (error) {
                console.error("Invalid token on load:", error);
                localStorage.removeItem('token');
            }
        }
    }, []); // Empty array ensures this runs only once on mount

    // Login function
    const login = useCallback(async (username, password) => {
        try {
            const response = await api.post('/auth/authenticate', { username, password });
            const { token: newToken } = response.data;
            
            localStorage.setItem('token', newToken);
            const payload = JSON.parse(atob(newToken.split('.')[1]));
            
            // 👇 --- FIX #2 ---
            // Clean the roles before setting the user
            const cleanedUserRoles = cleanRoles(payload.authorities);

            setUser({
                username: payload.sub,
                roles: cleanedUserRoles // Use the cleaned roles
            });
            
            return { success: true };
        } catch (error) {
            console.error("Login failed:", error);
            return { success: false, message: error.message || "Login failed" };
        }
    }, []);

    // Logout function
    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('token');
        // You might want to redirect to login page here
        // window.location.href = '/login';
    }, []);

    // Derived role helpers. AUDITOR is a read-only role: it can view Dashboard,
    // Invoice, Vendor and TDS screens but must not see edit/create/delete actions.
    const isAuditor = !!user?.roles?.includes('AUDITOR');

    // Value provided to children
    const value = {
        user,
        login,
        logout,
        isAuthenticated: !!user,
        isAuditor,
        // Convenience flag for gating any mutating UI control.
        canEdit: !isAuditor
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};